import React, { useState, useEffect } from 'react';
import { Typography, Button } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import { rootUrl } from '../../../../config/config';
import PartDialog from '../components/PartDialog';

export default function JobCardPartsList({ jobCardId, jobCard, locked, onRefresh }) {
    const { user } = useAuth();
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPart, setEditingPart] = useState(null);
    const [deleting, setDeleting] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchParts();
    }, [jobCardId]);

    const fetchParts = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/api/job-cards/${jobCardId}/parts`, {
                headers: { Authorization: `Bearer ${user?.token}` }
            });
            setParts(response.data?.data || response.data || []);
        } catch (error) {
            console.error('Error fetching parts:', error);
            setParts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddPart = () => {
        setEditingPart(null);
        setDialogOpen(true);
    };

    const handleEditPart = (part) => {
        setEditingPart(part);
        setDialogOpen(true);
    };

    const handleDeletePart = async (partId) => {
        if (!window.confirm('Are you sure you want to remove this part?')) return;

        try {
            setDeleting(partId);
            await axios.delete(`${rootUrl}/api/job-cards/${jobCardId}/parts/${partId}`, {
                headers: { Authorization: `Bearer ${user?.token}` }
            });
            await fetchParts();
            onRefresh?.();
        } catch (error) {
            console.error('Error deleting part:', error);
            alert('Failed to delete part');
        } finally {
            setDeleting(null);
        }
    };

    const handlePartAdded = () => {
        setDialogOpen(false);
        setEditingPart(null);
        fetchParts();
        onRefresh?.();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} spin className="w-6 h-6 text-blue-600 mr-3" />
                <Typography>Loading parts...</Typography>
            </div>
        );
    }

    return (
        <div className="py-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                <Typography variant="h6" className="text-gray-900">
                    Job Card Parts
                </Typography>
                <Button
                    size="sm"
                    color="blue"
                    onClick={handleAddPart}
                    disabled={locked}
                    className="w-full sm:w-auto text-xs md:text-sm"
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add Part
                </Button>
            </div>

            {/* Parts List */}
            {locked && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-300 mb-4">
                    <Typography className="text-red-700 text-sm font-semibold">
                        This job card is locked. You cannot add, edit, or remove parts.
                    </Typography>
                </div>
            )}



            {parts.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <Typography color="gray" className="text-sm">
                        No parts added yet. Click "Add Part" to get started.
                    </Typography>
                </div>
            ) : (
                <>
                    {/* Desktop: Table */}
                    <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 font-semibold text-gray-700">Part Name</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700">Part Number</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700">Qty</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Unit Cost</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Total</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parts.map((part, idx) => {
                                    const total = (part.quantity_used || 0) * (part.unit_cost || 0) - (part.discount_amount || 0);
                                    return (
                                        <tr key={part.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                {part.part_name || 'N/A'}
                                                {part.serials?.length > 0 && (
                                                    <div className="text-xs text-gray-500 font-mono mt-1">S/N: {part.serials.join(', ')}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">{part.part_number || 'N/A'}</td>
                                            <td className="px-4 py-3">{part.quantity_used || 0}</td>
                                            <td className="px-4 py-3 text-right">{formatCurrency(part.unit_cost)}</td>
                                            <td className="px-4 py-3 text-right font-semibold">{formatCurrency(total)}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="text"
                                                        color="blue"
                                                        onClick={() => handleEditPart(part)}
                                                        disabled={locked}
                                                        className="p-1"
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="text"
                                                        color="red"
                                                        onClick={() => handleDeletePart(part.id)}
                                                        disabled={locked || deleting === part.id}
                                                        className="p-1"
                                                    >
                                                        {deleting === part.id ? (
                                                            <FontAwesomeIcon icon={faSpinner} spin className="w-4 h-4" />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile: Cards */}
                    <div className="md:hidden space-y-3">
                        {parts.map((part, idx) => {
                            const total = (part.quantity_used || 0) * (part.unit_cost || 0) - (part.discount_amount || 0);
                            return (
                                <div key={part.id || idx} className="bg-white rounded-lg border border-gray-200 p-4 space-y-2">
                                    <div className="flex justify-between items-start gap-2">
                                        <div>
                                            <Typography variant="h6" className="text-gray-900 break-words">
                                                {part.part_name || 'N/A'}
                                            </Typography>
                                            <Typography variant="small" className="text-gray-600">
                                                {part.part_number || 'N/A'}
                                            </Typography>
                                            {part.serials?.length > 0 && (
                                                <Typography variant="small" className="text-xs text-gray-500 font-mono">
                                                    S/N: {part.serials.join(', ')}
                                                </Typography>
                                            )}
                                        </div>
                                        <div className="flex gap-1">
                                            <Button
                                                size="sm"
                                                variant="text"
                                                color="blue"
                                                onClick={() => handleEditPart(part)}
                                                disabled={locked}
                                                className="p-1"
                                            >
                                                <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="text"
                                                color="red"
                                                onClick={() => handleDeletePart(part.id)}
                                                disabled={locked || deleting === part.id}
                                                className="p-1"
                                            >
                                                {deleting === part.id ? (
                                                    <FontAwesomeIcon icon={faSpinner} spin className="w-4 h-4" />
                                                ) : (
                                                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <Typography variant="small" className="text-gray-600">Quantity</Typography>
                                            <Typography variant="small" className="font-semibold">{part.quantity_used || 0}</Typography>
                                        </div>
                                        <div>
                                            <Typography variant="small" className="text-gray-600">Unit Cost</Typography>
                                            <Typography variant="small" className="font-semibold">{formatCurrency(part.unit_cost)}</Typography>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-gray-100">
                                        <Typography variant="small" className="text-gray-600">Total</Typography>
                                        <Typography variant="h6" className="text-blue-600">{formatCurrency(total)}</Typography>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Summary */}
                    <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <Typography variant="small" className="text-gray-700 mb-2">
                            Total Parts Cost:
                        </Typography>
                        <Typography variant="h5" className="text-blue-600 font-bold">
                            {formatCurrency(
                                parts.reduce((sum, p) => {
                                    const total = (p.quantity_used || 0) * (p.unit_cost || 0) - (p.discount_amount || 0);
                                    return sum + total;
                                }, 0)
                            )}
                        </Typography>
                    </div>
                </>
            )}

            {/* Part Dialog */}
            <PartDialog
                open={dialogOpen}
                editingPart={editingPart}
                jobCardId={jobCardId}
                serviceLocation={jobCard?.service_location}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingPart(null);
                }}
                onPartAdded={handlePartAdded}
            />
        </div>
    );
}
